import { useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useFormikContext } from 'formik'

import Form from '@/components/Forms/Form'
import InputField from '@/components/Forms/InputField'
import PasswordField from '@/components/Forms/PasswordField'
import RememberMe from '@/components/RememberMe'
import styles from '@/components/LoginPage.module.scss'
import useRoutes from '@/hooks/useRoutes'
import FieldError from '@/components/Forms/FieldError'
import { Button, Link, Spinner } from '@/components/common'
import { getMessage } from '@/utils/messages'
import { LOGIN_TYPE, URLS } from '@/constants'
import useScope from '@/hooks/useScope'
import useAuth from '@/hooks/useAuth'
import { getUserName, clearUserName } from '@/utils/user'

const LoginForm = ({ disabled = false, isLoading = false, loginType, recaptchaError, submitDisabled = false }) => {
  const formik = useFormikContext()
  const routes = useRoutes()
  const { scope } = useScope()
  const { dirty, isSubmitting, values } = formik
  const { handleRememberMeChange, rememberMe, canStoreUsername, shouldShowRememberMe } = useAuth()
  const lastUsername = useRef(values.username)

  const handleUsernameChange = useCallback((username) => {
    if (!shouldShowRememberMe || !canStoreUsername) {
      return
    }
    const storedUsername = getUserName()
    const nextRememberMe = !!username && (!!storedUsername && (username === storedUsername))
    const userNameChanged = (lastUsername.current !== username)

    // only reset rememberMe when username !== stored username
    if (storedUsername && userNameChanged && (nextRememberMe !== rememberMe)) {
      clearUserName()
      handleRememberMeChange(false)
    }

    lastUsername.current = values.username
  }, [shouldShowRememberMe, scope, rememberMe, handleRememberMeChange, canStoreUsername])

  return (
    <Form>
      <InputField
        name="username"
        labelProps={{ label: getMessage('labels.username') }}
        placeholder={getMessage('placeholders.username')}
        type="email"
        disabled={disabled}
        size="large"
        onChange={handleUsernameChange}
      />
      <PasswordField
        name="password"
        labelProps={{ label: getMessage('labels.password') }}
        size="large"
      />
      <FieldError
        ignoreTouched
        message={!isSubmitting && !isLoading && recaptchaError
          ? (
            <>
              {getMessage('login.recaptchaError')}&nbsp;
              <Link
                external
                openTab
                size="xsmall"
                to={URLS.contactSupport}
                messageId="login.contactSupport"
              />
            </>
          )
          : undefined}
        className={styles.formError}
      />
      <div className={styles.actions}>
        <Button
          size="large"
          type="submit"
          disabled={!dirty || isSubmitting || isLoading || submitDisabled}
          fill
          messageId={`button.${loginType === LOGIN_TYPE.invitation ? 'accept' : 'signIn'}`}
        />
      </div>
      <div className={styles.signUpLink}>
        <Link to={routes.recoverPassword.url()} size="xsmall" messageId="button.resetPassword" />
      </div>
      {shouldShowRememberMe && (
      <div className={styles.rememberMe}>
        <RememberMe disabled={isLoading} />
      </div>
      )}
      {isSubmitting && <Spinner />}
    </Form>
  )
}

LoginForm.propTypes = {
  loginType: PropTypes.oneOf(Object.values(LOGIN_TYPE)).isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  recaptchaError: PropTypes.string,
  submitDisabled: PropTypes.bool,
}

export default LoginForm
